import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Dialog,
    DialogContent,
} from "@material-ui/core";
import Select from "react-select";
import chroma from "chroma-js";
import COLORS from "scss/colors.module.scss";
import ApiClient from "utils/ApiClient";
import {
    ColumnName,
    SavingSpinnerForColumnEditors,
    ActionButtonsForColumnEditors,
    handleClose,
    ErrorMessageViewer,
    DropdownIndicator,
    getColumnDefById
} from "./ColumnEditorFactory";
import { formatDate, formatTime } from "../../helpers/dateFormatter";
import { validateDropdownInputsBeforeSaving } from "../RowId/Validations";
import { OptionFormatter } from "../Lookup/LookupColumnEditor";

const LookupReusableEditor = ({
    initialValue = null,
    open,
    store,
    data,
    column,
    columnName,
    onSave,
    onCancel
}) => {
    const [options, setOptions] = useState([
        {
            value: "Loading ...",
            label: "Loading ...",
            color: COLORS.TEXT_COLOR,
            background: COLORS.PRIMARY_COLOR,
        },
    ]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [validationError, setValidationError] = useState({ isInvalid: false, message: "" });
    const [isSaving, setIsSaving] = useState(false);

    const selectRef = useRef(null);

    const columnOptions = column?.normalizedColumnOptions ?? {};
    const isSubsheet = columnOptions?.isSubsheet ?? false;
    // const columnDef = getColumnDefById(column?.id);
    // const lookupStore = columnDef?.cellRendererParams?.store ?? store ?? window.appStore?.containerStore ?? {};
    const referencedRowIdForInitialValue = data['column_'+ column?.id + '_reference_id'] ?? "";

    const findQueryParams = (colId) => {
        const rowData = data;
        let val = rowData?.[`column_${colId}`];

        if (isSubsheet && !val) {
            return store?.parentRow?.[`column_${colId}`] ?? "";
        }

        return val;
    };

    const getLookupData = async () => {
        try {
            const queryParams = {};
            columnOptions?.query_params?.forEach((colId) => {
                queryParams[colId] = findQueryParams(colId);
            });

            const resp = await ApiClient.List.getLookupColumnData({
                column_id: column?.id,
                query_params: JSON.stringify(queryParams),
            });

            return resp.data || [];
        } catch (e) {
            console.error("Error fetching lookup data", e);
            return [];
        }
    };

    const getFormattedValueByColumnType = (value, columnType) => {
        try {
            let formattedValue = value;
            let fileNames = "";
            if (columnType === "percentage" && String(value).length > 0 && String(value).includes(".")) {
                let decimals = String(value).split(".")[1]?.length >= 2 ? String(value).split(".")[1].length : 2;
                formattedValue = (Number(value) * 100).toFixed(decimals - 2) + " %";
            }

            if (
                (columnType === "date" || columnType === "datetime") &&
                String(value).length > 0
            ) {
                let sourceColumnTypeID =
                columnType === "date" ? "4" : "16";
                let dateFormat = formatDate(
                    value,
                    "YYYY-MM-DD",
                    columnOptions?.format
                        ? columnOptions?.format
                        : sourceColumnTypeID === "4"
                        ? "M j, Y"
                        : "M j, Y g:iA"
                );
                let timeFormat = formatTime(value, sourceColumnTypeID);
                formattedValue = sourceColumnTypeID === "4"
                        ? dateFormat
                        : dateFormat + " " + timeFormat;
            }

            if (columnType === "file") {
                try {
                    const imagesArray = JSON.parse(value || "[]");
                    fileNames = imagesArray.map((i) => i.name).join(" ");
                } catch {
                    fileNames = "";
                }
            }

            return { formattedValue, fileNames };
        } catch (error) {
            console.log("Error formatting value:", error);
            return { formattedValue: value, fileNames: "" };
        }
    };

    const buildOptions = (lookupData) => {
        const opts = [
            {
                reference_column_id: "",
                value: "",
                label: "",
                color: COLORS.TEXT_COLOR,
                background: "transparent",
            },
        ];

        lookupData.forEach((option) => {
            let value = option[`column_${columnOptions.source_column_id}`];
            const { formattedValue, fileNames } = getFormattedValueByColumnType(value, columnOptions?.column_type);

            opts.push({
                reference_column_id: option?.row_id,
                value,
                label: columnOptions?.column_type === "file" ? fileNames : formattedValue,
                color: COLORS.TEXT_COLOR,
                background: COLORS.PRIMARY_COLOR,
            });
        });

        return opts;
    };

    useEffect(() => {
        if (open) {
            setSelectedOption(null);
            setOptions([
                {
                    value: "Loading ...",
                    label: "Loading ...",
                    color: COLORS.TEXT_COLOR,
                    background: COLORS.PRIMARY_COLOR,
                },
            ]);

            (async () => {
                const lookupData = await getLookupData();
                const opts = buildOptions(lookupData);
                setOptions(opts);

                if (initialValue) {
                    const match = opts.find((o) => (o.reference_column_id == referencedRowIdForInitialValue));
                    setSelectedOption(match || null);
                }
            })();
        }
        return () => {
            setValidationError({ isInvalid: false, message: "" });
        }
    }, [open, initialValue]);

    const handleChange = (selected) => {
        if (selected?.value !== "Loading ...") {
            setSelectedOption(selected);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            if (!selectedOption) {
                    onCancel();
                    return
            };

            if (selectedOption.value === initialValue && selectedOption?.reference_column_id == referencedRowIdForInitialValue) {
                onCancel();
                return;
            }

            const isUpdateAllowed = validateDropdownInputsBeforeSaving(selectedOption?.reference_column_id, column, data);
            if (isUpdateAllowed.isInvalid) {
                setValidationError(isUpdateAllowed);
                return;
            }

            await onSave(
                {
                    reference_column_id: selectedOption?.reference_column_id,
                    value: selectedOption.value,
                },
                initialValue,
                column
            );
        } catch (err) {
            console.error("Error saving lookup value:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            minWidth: "100%",
            marginTop: 8,
        }),
        menuPortal: base => ({
            ...base,
            zIndex: 2000,
        }),
        menu: (base) => ({
            ...base,
            zIndex: 2000,
            maxHeight: "250px",
            overflowY: "auto",
        }),
        option: (provided, { isFocused, isSelected }) => {
            const color = chroma(COLORS.THEME_COLOR);
            return {
                ...provided,
                minHeight: 32,
                display: "flex",
                alignItems: "center",
                backgroundColor: isFocused
                    ? color.alpha(0.1).css()
                    : isSelected
                    ? "transparent"
                    : null,
                color: COLORS.TEXT_COLOR,
            };
        },
    };

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(selectedOption?.reference_column_id, referencedRowIdForInitialValue, onCancel, validationError, setValidationError)}
            fullWidth
            PaperProps={{
                style: {
                width: validationError.isInvalid ? "450px" : "420px",
                height: validationError.isInvalid ? "200px" : "180px",
                borderRadius: "8px",
                },
            }}
        >
            <DialogContent>
                <Box display="flex" flexDirection="column" style={{ width: "100%", gap: 8 }}>
                    {columnName && <ColumnName columnName={columnName} />}

                    <Select
                        ref={selectRef}
                        value={selectedOption}
                        onChange={handleChange}
                        options={options}
                        hideSelectedOptions={false}
                        isClearable={false}
                        placeholder="Find an option..."
                        styles={selectStyles}
                        components={{
                            DropdownIndicator: DropdownIndicator,
                            IndicatorSeparator: null,
                        }}
                        // formatOptionLabel={({ label }) => <div style={{ display: "flex" }}>{label}</div>}
                        formatOptionLabel={(props) => (
                            <OptionFormatter
                              {...props}
                              columnWidth={validationError.isInvalid ? "402px" : "372px"}
                            />
                          )}
                        maxMenuHeight={150}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                    />

                    {validationError.isInvalid && (<ErrorMessageViewer errMsg={validationError.message} />)}

                    <ActionButtonsForColumnEditors
                        handleSave={handleSave}
                        handleCancel={() => handleClose(selectedOption?.reference_column_id, referencedRowIdForInitialValue, onCancel, validationError, setValidationError)}
                        isSaveDisabled={isSaving}
                        isCloseDisabled={isSaving}
                    />

                    {isSaving && <SavingSpinnerForColumnEditors />}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default LookupReusableEditor;